import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Box,
  MapPin,
  Loader2,
  Building2,
  FolderOpen,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Master {
  id: string;
  code: string;
  name: string;
  description: string | null;
  material: string | null;
  dimensions: string | null;
  weight_kg: number | null;
  location: string | null;
  status: string;
  project_id: string | null;
  customer_id: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  project?: { name: string };
  customer?: { company_name: string };
}

const STATUS_OPTIONS = [
  { value: 'available', label: 'Disponible', color: 'bg-green-500' },
  { value: 'in_use', label: 'En uso', color: 'bg-blue-500' },
  { value: 'maintenance', label: 'Mantenimiento', color: 'bg-amber-500' },
  { value: 'retired', label: 'Retirado', color: 'bg-gray-500' },
];

export default function Masters() {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState<Master | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    material: '',
    dimensions: '',
    weight_kg: '',
    location: '',
    status: 'available',
    project_id: '',
    customer_id: '',
    notes: '',
  });

  const { data: masters = [], isLoading } = useQuery({
    queryKey: ['masters'],
    queryFn: async () => {
      const { data, error } = await api
        .from('masters')
        .select('*, project:projects(name), customer:customers(company_name)')
        .eq('is_active', true)
        .order('code');
      if (error) throw error;
      return data as Master[];
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ['projects-select'],
    queryFn: async () => {
      const { data } = await api.from('projects').select('id, name').neq('status', 'cancelled').order('name');
      return data || [];
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers-select'],
    queryFn: async () => {
      const { data } = await api.from('customers').select('id, company_name').eq('is_active', true).order('company_name');
      return data || [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        weight_kg: data.weight_kg ? parseFloat(data.weight_kg) : null,
        project_id: data.project_id === '__none__' ? null : (data.project_id || null),
        customer_id: data.customer_id === '__none__' ? null : (data.customer_id || null),
        created_by: user?.id,
      };

      if (editingMaster) {
        const { error } = await api.from('masters').update(payload).eq('id', editingMaster.id);
        if (error) throw error;
      } else {
        const { error } = await api.from('masters').insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast({ title: editingMaster ? 'Máster actualizado' : 'Máster creado' });
      queryClient.invalidateQueries({ queryKey: ['masters'] });
      closeDialog();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from('masters').update({ is_active: false }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Máster eliminado' });
      queryClient.invalidateQueries({ queryKey: ['masters'] });
    },
  });

  const openCreateDialog = () => {
    setEditingMaster(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      material: '',
      dimensions: '',
      weight_kg: '',
      location: '',
      status: 'available',
      project_id: '',
      customer_id: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (master: Master) => {
    setEditingMaster(master);
    setFormData({
      code: master.code,
      name: master.name,
      description: master.description || '',
      material: master.material || '',
      dimensions: master.dimensions || '',
      weight_kg: master.weight_kg?.toString() || '',
      location: master.location || '',
      status: master.status,
      project_id: master.project_id || '',
      customer_id: master.customer_id || '',
      notes: master.notes || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingMaster(null);
  };

  const handleSubmit = () => {
    if (!formData.code || !formData.name) {
      toast({ title: 'Error', description: 'Código y nombre son obligatorios', variant: 'destructive' });
      return;
    }
    saveMutation.mutate(formData);
  };

  const filteredMasters = masters.filter(m =>
    m.code.toLowerCase().includes(search.toLowerCase()) ||
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.location?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const config = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={config.color + ' text-white text-xs'}>{config.label}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 p-2 sm:p-0">
        {/* Header - responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3">
              <Box className="w-6 h-6 sm:w-8 sm:h-8" />
              Másters
            </h1>
            <p className="text-sm text-muted-foreground">Modelos maestros para fabricación de moldes</p>
          </div>
          <Button onClick={openCreateDialog} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Máster
          </Button>
        </div>

        {/* Search and count */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Badge variant="outline" className="self-start sm:self-auto">
                {filteredMasters.length} másters
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : filteredMasters.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay másters registrados
              </div>
            ) : (
              <>
                {/* Mobile: Cards view */}
                <div className="block sm:hidden space-y-3">
                  {filteredMasters.map((master) => (
                    <Card key={master.id} className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-mono font-medium text-sm">{master.code}</span>
                            {getStatusBadge(master.status)}
                          </div>
                          <p className="font-medium truncate">{master.name}</p>
                          {master.material && (
                            <p className="text-sm text-muted-foreground">{master.material}</p>
                          )}
                          {master.location && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {master.location}
                            </p>
                          )}
                          {(master.customer || master.project) && (
                            <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                              {master.customer && (
                                <p className="flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  {master.customer.company_name}
                                </p>
                              )}
                              {master.project && (
                                <p className="flex items-center gap-1">
                                  <FolderOpen className="w-3 h-3" />
                                  {master.project.name}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(master)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-500"
                              onClick={() => deleteMutation.mutate(master.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Desktop: Table view */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Material</TableHead>
                        <TableHead>Ubicación</TableHead>
                        <TableHead>Cliente/Proyecto</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMasters.map((master) => (
                        <TableRow key={master.id}>
                          <TableCell className="font-mono font-medium">{master.code}</TableCell>
                          <TableCell>{master.name}</TableCell>
                          <TableCell>{master.material || '-'}</TableCell>
                          <TableCell>
                            {master.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {master.location}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {master.customer && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Building2 className="w-3 h-3" />
                                  {master.customer.company_name}
                                </span>
                              )}
                              {master.project && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <FolderOpen className="w-3 h-3" />
                                  {master.project.name}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(master.status)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(master)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => deleteMutation.mutate(master.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog - responsive with scroll */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-4 sm:p-6 pb-2">
            <DialogTitle>{editingMaster ? 'Editar Máster' : 'Nuevo Máster'}</DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 px-4 sm:px-6">
            <div className="grid gap-4 py-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Código *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="MST-001"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del máster"
                />
              </div>

              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Material</Label>
                  <Input
                    value={formData.material}
                    onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                    placeholder="Ej: Resina, Fibra..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dimensiones</Label>
                  <Input
                    value={formData.dimensions}
                    onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                    placeholder="Ej: 100x50x30 cm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Peso (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ubicación</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Ej: Estantería A3"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin cliente</SelectItem>
                      {customers.map((c: any) => (
                        <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Proyecto</Label>
                  <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Sin proyecto</SelectItem>
                      {projects.map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="p-4 sm:p-6 pt-2 border-t">
            <div className="flex flex-col-reverse sm:flex-row gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={closeDialog} className="w-full sm:w-auto">
                Cancelar
              </Button>
              <Button onClick={handleSubmit} disabled={saveMutation.isPending} className="w-full sm:w-auto">
                {saveMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingMaster ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
